// Admin Dashboard Theme - Consistent with Landing Page
// These styles match the LandingPageEnhanced.tsx styling

export const adminTheme = {
  // Primary colors (from landing page gradient)
  colors: {
    primary: '#6366F1',
    primaryDark: '#4F46E5',
    primaryLight: '#8B5CF6',

    // Secondary/accent colors
    success: '#10B981',
    successLight: '#34D399',
    warning: '#F59E0B',
    warningLight: '#FBBF24',
    error: '#EF4444',
    errorLight: '#F87171',
    info: '#06B6D4',
    infoLight: '#22D3EE',

    // Neutral colors
    dark: '#0F172A',
    darkSecondary: '#1E293B',
    textPrimary: '#0F172A',
    textSecondary: '#64748B',
    textMuted: '#94A3B8',

    // Background colors
    background: '#FFFFFF',
    backgroundAlt: '#F8FAFC',
    backgroundDark: '#0F172A',

    // Border colors
    border: '#E2E8F0',
    borderLight: '#F1F5F9',
  },

  // Gradients (from landing page)
  gradients: {
    primary: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
    primaryHover: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
    success: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
    warning: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
    error: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)',
    dark: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
    background: 'linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)',
  },

  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
    primary: '0 8px 24px -8px rgba(99, 102, 241, 0.5)',
    card: '0 4px 20px -4px rgba(0, 0, 0, 0.05)',
    cardHover: '0 20px 40px -12px rgba(0, 0, 0, 0.1)',
  },

  // Border radius
  borderRadius: {
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    full: '9999px',
  },

  // Spacing (consistent with MUI)
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    '2xl': '48px',
  },

  // Typography
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
    },
  },

  // Transitions
  transitions: {
    fast: 'all 0.15s ease',
    normal: 'all 0.2s ease',
    slow: 'all 0.3s ease',
  },
};

// Common card styles
export const cardStyles = {
  default: {
    borderRadius: adminTheme.borderRadius.lg,
    border: `1px solid ${adminTheme.colors.border}`,
    boxShadow: adminTheme.shadows.card,
    backgroundColor: adminTheme.colors.background,
    transition: adminTheme.transitions.normal,
  },
  hover: {
    transform: 'translateY(-4px)',
    boxShadow: adminTheme.shadows.cardHover,
    borderColor: 'transparent',
  },
  interactive: {
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: adminTheme.shadows.cardHover,
    },
  },
};

// Common button styles
export const buttonStyles = {
  primary: {
    background: adminTheme.gradients.primary,
    color: '#FFFFFF',
    fontWeight: 600,
    borderRadius: adminTheme.borderRadius.sm,
    textTransform: 'none' as const,
    '&:hover': {
      background: adminTheme.gradients.primaryHover,
    },
  },
  outlined: {
    borderColor: adminTheme.colors.border,
    color: adminTheme.colors.textSecondary,
    fontWeight: 500,
    borderRadius: adminTheme.borderRadius.sm,
    textTransform: 'none' as const,
    '&:hover': {
      borderColor: adminTheme.colors.primary,
      backgroundColor: 'rgba(99, 102, 241, 0.05)',
    },
  },
};

// Page container styles
export const pageContainerStyles = {
  background: adminTheme.colors.backgroundAlt,
  minHeight: '100vh',
  padding: '24px',
};

// Section header styles
export const sectionHeaderStyles = {
  title: {
    fontWeight: 700,
    color: adminTheme.colors.textPrimary,
    marginBottom: '8px',
  },
  subtitle: {
    color: adminTheme.colors.textSecondary,
    fontWeight: 400,
  },
};

// Table styles
export const tableStyles = {
  container: {
    borderRadius: adminTheme.borderRadius.lg,
    border: `1px solid ${adminTheme.colors.border}`,
    backgroundColor: adminTheme.colors.background,
    overflow: 'hidden',
  },
  header: {
    backgroundColor: adminTheme.colors.backgroundAlt,
    '& th': {
      fontWeight: 600,
      color: adminTheme.colors.textPrimary,
      borderBottom: `2px solid ${adminTheme.colors.border}`,
    },
  },
  row: {
    '&:hover': {
      backgroundColor: 'rgba(99, 102, 241, 0.04)',
    },
  },
};

// Status badge colors
export const statusColors = {
  active: { bg: '#DCFCE7', text: '#166534' },
  inactive: { bg: '#FEE2E2', text: '#991B1B' },
  pending: { bg: '#FEF3C7', text: '#92400E' },
  completed: { bg: '#DBEAFE', text: '#1E40AF' },
  in_progress: { bg: '#E0E7FF', text: '#3730A3' },
  inprogress: { bg: '#E0E7FF', text: '#3730A3' },
  finished: { bg: '#DCFCE7', text: '#166534' },
  cancelled: { bg: '#FEE2E2', text: '#991B1B' },
  failed: { bg: '#FEE2E2', text: '#991B1B' },
  succeed: { bg: '#DCFCE7', text: '#166534' },
  prepared: { bg: '#FEF3C7', text: '#92400E' },
  deal_done: { bg: '#DCFCE7', text: '#166534' },
  dealdone: { bg: '#DCFCE7', text: '#166534' },
};

// Status labels in Lithuanian
export const statusLabels: Record<string, string> = {
  // Process execution statuses
  IN_PROGRESS: 'Vykdoma',
  FINISHED: 'Užbaigta',
  CANCELLED: 'Atšaukta',
  DEAL_DONE: 'Užbaigta',
  // Step statuses
  ACTIVE: 'Aktyvus',
  COMPLETED: 'Užbaigta',
  PENDING: 'Laukiama',
  // Email statuses
  SUCCEED: 'Išsiųsta',
  FAILED: 'Nepavyko',
  PREPARED: 'Paruošta',
  // General
  ACTIVE_STATUS: 'Aktyvus',
  INACTIVE: 'Neaktyvus',
};

// Helper function to get translated status label
export function getStatusLabel(status: string | undefined | null): string {
  if (!status) return '';
  return statusLabels[status.toUpperCase()] || status.replace(/_/g, ' ');
}

export default adminTheme;
